'use client';

import { c as _c } from "react/compiler-runtime";
import type { Row } from '@tanstack/react-table';
import { InterfaceChainUtils } from '@morpho-org/dapp-core';
import { Card, Skeleton } from '@morpho-org/ui-v6/server';
import { Separator } from '@repo/ui';
import { MobileMarketCard } from '@/components/common/MobileBorrowCard';
import { getMarketUrl } from '@/helpers/routing';
import type { TableMarketFeatures } from '../config/TableMarket.columns';
import type { TableMarketRow } from '../config/TableMarket.types';
interface MobileMarketCardListProps {
  rows: Row<TableMarketFeatures, TableMarketRow>[];
  isLoading: boolean;
  isFetchingMore: boolean;
  skeletonCount: number;
}
function MobileMarketCardItem({
  row
}: {
  row: Row<TableMarketFeatures, TableMarketRow>;
}) {
  // 'use no memo': TanStack keeps this object's reference while table state (sorting, rows) changes.
  'use no memo';

  const {
    market,
    lltv,
    borrowAssetsUsd,
    totalLiquidityUsd,
    avgNetBorrowApy,
    netSupplyApy,
    trustedBy
  } = row.original;
  const href = getMarketUrl({
    identifier: InterfaceChainUtils.getIdentifierById(market.chainId),
    marketId: market.marketId,
    collateralSymbol: market.collateralAsset?.symbol ?? '',
    loanSymbol: market.loanAsset.symbol ?? ''
  });
  return <MobileMarketCard href={href} chainId={market.chainId} lltv={lltv} collateral={{
    token: market.collateralAsset
  }} loan={{
    token: market.loanAsset
  }} marketId={market.marketId} totalLiquidity={totalLiquidityUsd} totalBorrow={borrowAssetsUsd} netApy={avgNetBorrowApy.avgNetBorrowApy} apy={avgNetBorrowApy.avgBorrowApy} rewards={avgNetBorrowApy.rewards} points={avgNetBorrowApy.points} supply={netSupplyApy} curators={trustedBy.curators} hasUserPosition={market.hasUserPosition} warnings={market.warnings} />;
}
function MobileMarketCardSkeleton() {
  const $ = _c(11);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex items-center justify-between"><div className="gap-xs flex items-center"><Skeleton variant="circle" className="size-5" /><Skeleton className="h-[16px] w-[120px]" /></div></div>;
    t1 = <Separator className="my-xs" />;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Skeleton className="h-[16px] w-[55px]" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="h-md flex items-center justify-between">{t2}<div className="gap-2xs flex items-center"><Skeleton variant="circle" className="size-[16px]" /><Skeleton className="h-[14px] w-[50px]" /></div></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="gap-sm flex items-center justify-between"><Skeleton className="h-[16px] w-20" /><Skeleton className="h-md w-[100px]" /></div>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="flex items-center justify-between"><Skeleton className="h-[16px] w-[60px]" /><Skeleton className="h-md w-[90px]" /></div>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="flex items-center justify-between"><Skeleton className="h-[16px] w-[50px]" /><Skeleton className="h-[17px] w-20" /></div>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="flex items-center justify-between"><Skeleton className="h-[16px] w-[60px]" /><Skeleton className="h-md w-20" /></div>;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="flex items-center justify-between"><Skeleton className="h-[16px] w-[60px]" /><Skeleton className="h-md w-20" /></div>;
    t9 = <Separator className="my-xs" />;
    $[8] = t8;
    $[9] = t9;
  } else {
    t8 = $[8];
    t9 = $[9];
  }
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Card data-testid="listing-card" padding="s" withBorder={true} className="gap-sm flex flex-col"><div className="gap-sm flex flex-col">{t0}{t1}{t3}{t4}{t5}{t6}{t7}{t8}{t9}<div className="gap-sm flex"><Skeleton className="h-lg flex-1 rounded-full" /></div></div></Card>;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  return t10;
}
function MobileMarketCardList({
  rows,
  isLoading,
  isFetchingMore,
  skeletonCount
}: MobileMarketCardListProps) {
  // 'use no memo': TanStack keeps this object's reference while table state (sorting, rows) changes.
  'use no memo';

  return <div className="gap-sm flex flex-col">
      {isLoading ? Array.from({
      length: skeletonCount
    }).map((_, index) => <MobileMarketCardSkeleton key={`skeleton-mobile-${index}`} />) : rows.map(row => <MobileMarketCardItem key={row.id} row={row} />)}
      {isFetchingMore && Array.from({
      length: skeletonCount
    }).map((__0, index_0) => <MobileMarketCardSkeleton key={`skeleton-more-${index_0}`} />)}
    </div>;
}
export default MobileMarketCardList;